import { Component,  EventEmitter,  Input,  Output,  SimpleChanges,  TemplateRef,  ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, map, Observable, firstValueFrom } from 'rxjs';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { fadeIn } from './camera-snapshot.animations';
import { transition, trigger, useAnimation } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { Device, fragQuery, Vehicle } from '@models';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { WebClient } from '@injectables';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { VehicleQueryService } from 'src/injectables/vehicle-query.service';
import { TiffToImageConverter } from '@app/utilities/snapshot/tiff-to-image-converter';
import { DialogDownloadImageComponent } from '@app/old-ui/dialog/dialog-download-image/dialog-download-image.component';
import { DateFormatConfig } from '@app/utilities/date-format-config';

@Component({
    selector: 'camera-snapshot',
    templateUrl: './camera-snapshot.component.html',
    styleUrls: ['./camera-snapshot.component.scss'],
    animations: [
        trigger('carouselAnimation', [
            transition('void => *', [useAnimation(fadeIn)]),
        ]),
    ],
})
export class CameraSnapshotComponent<
    Instance extends { id: number; name?: string },
    RestTypes extends WebClient.RestTypes
> {
    [x: string]: any;
    constructor(
        private readonly fragQuery: FragQueryService,
        private readonly vehicleQuery: VehicleQueryService,
        public dialogue: MatDialog,
        protected readonly dialog: DialogService,
        private readonly router: Router,
        private readonly dateFormat: DateFormatConfig
    ) {
        this._initCurrentIntervalSync();
    }

    @Input() imgSrc: string = '';
    @Input() snapType: string = 'home';
    @Input() deviceId: number;
    @Input() device: Device;

    @Output() nextClick = new EventEmitter<void>();
    @Output() previousClick = new EventEmitter<void>();

    @ViewChild('fullimgtemplate', { static: false }) fullimgtemplate!: TemplateRef<any>;

    public intervals$ = new BehaviorSubject<fragQuery.timeline.sieveSizesAtPercents.Interval[]>([]);
    public currentIntervalIndex$ = new BehaviorSubject<number | null>(null);
    public currentInterval$ = new BehaviorSubject<fragQuery.timeline.sieveSizesAtPercents.Interval | null>(null);

    public readonly device$ = this.fragQuery.device$;
    public readonly muckpile$ = this.fragQuery.muckpile$;
    public readonly vehicles$ = this.vehicleQuery.vehicles$;
    public readonly dateTimeFormat = this.dateFormat.getFormat();

    public imageLoadError = false;
    public defaultImageSrc = '../../../assets/image_not_found.jpg';
    public imageLoading = true;
    public imgsrc = '';
    public muckpileId: number = null;
    public baseImageUrl: string | null = null;
    public mergedImageUrl: string | null = null;
    public showMergedImage = true;
    public isTiff = false;
    public taggedVehicle$: Observable<Vehicle>;

    public hoverButtons = [
      { icon: 'full-screen-white', action: 'showFullImg', tooltip: 'Full Screen', disable: false },
      { icon: 'trash', action: 'delete', tooltip: 'Delete', disable: false },
      { icon: 'psd-white', action: 'switchPage', tooltip: 'Show PSD Details', disable: false },
      { icon: 'download-white', action: 'downloadImage', tooltip: 'Download Image', disable: this.imageLoadError }
    ];

    // === Inputs with setters/getters ===
    @Input()
    get intervals() {
        return this.intervals$.value;
    }
    set intervals(v) {
        this.intervals$.next(v);
    }

    @Input()
    get currentIntervalIndex() {
        return this.currentIntervalIndex$.value;
    }
    set currentIntervalIndex(v: number) {
        this.currentIntervalIndex$.next(v);
    }

    ngOnChanges(changes: SimpleChanges) {
        
        if (changes['intervals']) {
            this.intervals$.next([...changes['intervals'].currentValue]);
        }

        if (changes['currentIntervalIndex']) {
            this.currentIntervalIndex$.next(changes['currentIntervalIndex'].currentValue);
        }

        const intervals = this.intervals$.value;
        const index = this.currentIntervalIndex$.value;

        if (intervals && index != null && index >= 0 && index < intervals.length) {
            const current = intervals[index];
            this.imgsrc = current.imageUrl
            this.currentInterval$.next(current);
        } else {
            this.currentInterval$.next(null);
        }
    }

    private _initCurrentIntervalSync() {
        combineLatest([
            this.intervals$,
            this.currentIntervalIndex$
        ]).pipe(
            filter(([intervals, index]) => Array.isArray(intervals) && intervals.length > 0 && index !== null)
        ).subscribe(async ([intervals, index]) => {
            const interval = intervals[index];
            this.currentInterval$.next(interval);
            this._resetImageState();

            if (interval?.imageUrl) {
                const imageUrl = interval.imageUrl;
                const isTiffFile = imageUrl.split('?')[0].toLowerCase().endsWith('.tif') || imageUrl.split('?')[0].toLowerCase().endsWith('.tiff');
                if (isTiffFile) {
                    this.isTiff = true;
                    await this.loadImagesFromTiff(imageUrl);
                } else {
                    this.isTiff = false;
                    this.imgsrc = imageUrl;
                    this.imageLoading = false;
                }
            } else {
                this.onImageLoadError();
            }

            this.muckpileId = interval?.muckpileId;
            this._findTaggedVehicle(interval?.vehicleId);
        });
    }

    private _findTaggedVehicle(id: number) {
        this.taggedVehicle$ = id
            ? this.vehicles$.pipe(map(vehicles => vehicles.find(vehicle => vehicle.id === id)))
            : new BehaviorSubject<Vehicle>(null);
    }

    private _resetImageState() {
        this.imageLoading = true;
        this.imageLoadError = false;
        this.isTiff = false;
        this.baseImageUrl = null;
        this.mergedImageUrl = null;
        this.imgsrc = '';
        this.showMergedImage = true;
    }

    private _resetImageErrors() {
        this.imageLoadError = false;
        this.imageLoading = true;
    }

    private async loadImagesFromTiff(tiffUrl: string) {
        this.imageLoading = true;
        this.baseImageUrl = null;
        this.mergedImageUrl = null;
        try {
            const { baseUrl, mergedUrl } = await TiffToImageConverter.getTiffImageUrls(tiffUrl);
            this.baseImageUrl = baseUrl;
            this.mergedImageUrl = mergedUrl;
            this.imgsrc = this.showMergedImage ? this.mergedImageUrl : this.baseImageUrl;
        } catch (error) {
            console.error('Failed to load images from TIFF:', error);
            this.onImageLoadError();
        } finally {
            this.imageLoading = false;
        }
    }

    public goNext() {
        this._resetImageErrors();
        this.nextClick.emit();
    }

    public goPrevious() {
        this._resetImageErrors();
        this.previousClick.emit();
    }

    public onImageLoad() {
        this.imageLoading = false;
        this.imageLoadError = false;
    }

    public onImageLoadError() {
        this.imageLoadError = true;
        this.imageLoading = false;
        this.imgsrc = this.defaultImageSrc;
    }

    public showFullImg() {
        if (this.isTiff) {
            this.imgsrc = this.showMergedImage ? this.mergedImageUrl : this.baseImageUrl;
        } else {
            this.imgsrc = this.currentInterval$.value?.imageUrl;
        }
        this.dialogue.open(this.fullimgtemplate, {
            width: '100vw',
            height: '100vh'
        });
    }

    public handleAction(action: string): void {
        switch (action) {
            case 'showFullImg':
                this.showFullImg();
                break;
            case 'showMuckpile':
                this.showMuckpile();
                break;
            case 'delete':
                this.delete();
                break;
            case 'switchPage':
                this.switchPage();
                break;
            case 'downloadImage':
                this._downloadImage();
                break;
            default:
            console.error('Unknown action:', action);
        }
    }

    public toggleTiffView(): void {
        this.showMergedImage = !this.showMergedImage;
        this.imgsrc = this.showMergedImage ? this.mergedImageUrl : this.baseImageUrl;
    }
  
    public switchPage() {                
        const currentInterval = this.currentInterval$.value;
        const params = {
            device: this.deviceId,
            site: this.device.siteId,
            interval: currentInterval?.id ?? null,
        };
        this.router.navigateByUrl(`/details#${JSON.stringify(params)}`);
    }
  
    public closeFullImg() {
        this.dialogue.closeAll();
    }

    public showMuckpile() {
        if (this.muckpileId){
            this.dialog.openShowMuckpile(this.muckpileId);
            this.fragQuery.taggedMuckpileId$.next(this.muckpileId);
        }
    }

    private async _downloadImage() {
        const interval = this.currentInterval$.value;
        if (!interval || !interval.begin) return;

        let urlToDownload: string;
        let imageType: 'base' | 'merged' = 'base';

        if (this.isTiff) {
            const dialogRef = this.dialogue.open(DialogDownloadImageComponent);
            const result = await firstValueFrom(dialogRef.afterClosed());

            if (!result) return; // User cancelled

            imageType = result;
            urlToDownload = (result === 'base') ? this.baseImageUrl : this.mergedImageUrl;
        } else {
            urlToDownload = interval.imageUrl;
        }

        if (urlToDownload) {
            const intervalDate = DateTime.fromJSDate(interval.begin);
            const formattedDate = intervalDate.toFormat("ccc_LLL_dd_yyyy_HH'h'mm'm'ss's'");
            let filename = `${this.device.name}-${formattedDate}.jpg`;
            if (this.isTiff) {
                filename = `${this.device.name}-${formattedDate}-${imageType}.jpg`;
            }

            const response = await fetch(urlToDownload);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = blobUrl;
            anchor.download = filename;
            anchor.click();
            window.URL.revokeObjectURL(blobUrl);
        }
    }

    public async delete() {
        try {
            const interval = this.intervals$.value[this.currentIntervalIndex$.value];            
            const confirmed = await this.dialog.confirm({
                message: `Are you sure you want to delete this interval?`,
            });

            if (confirmed) {
                await this.dialog.await(
                    this.fragQuery.removeDataIntervals([interval.id])
                );
            }
        } catch (error) {
            alert('Failed to delete intervals. Please try again.');
        }
    }
}